import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

// Module-level cache to avoid refetching directory across many widgets/tabs.
let _cache = null;
let _cachePromise = null;
export function _resetEmployeeDirectoryCache() { _cache = null; _cachePromise = null; }

async function loadDirectory() {
  if (_cache) return _cache;
  if (_cachePromise) return _cachePromise;
  _cachePromise = api.get("/hr/directory")
    .then((r) => { _cache = r.data || []; _cachePromise = null; return _cache; })
    .catch(() => { _cachePromise = null; return []; });
  return _cachePromise;
}

/**
 * Free-form text input with an employee suggestion dropdown backed by
 * /api/hr/directory. Keeping free-form makes it backwards-compatible with any
 * pre-existing string values (e.g. legacy tasks without a linked employee).
 */
export default function EmployeeAutocomplete({
  value = "",
  onChange,
  placeholder = "",
  testid,
  className = "",
  disabled = false,
}) {
  const [directory, setDirectory] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value); }, [value]);
  useEffect(() => { loadDirectory().then(setDirectory); }, []);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = q
    ? directory.filter((e) => {
        const needle = q.toLowerCase();
        return e.name.toLowerCase().includes(needle)
          || (e.position || "").toLowerCase().includes(needle)
          || (e.dni || "").toLowerCase().includes(needle);
      }).slice(0, 8)
    : directory.slice(0, 8);

  const pick = (emp) => {
    onChange?.(emp.name);
    setQ(emp.name);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <Input
        data-testid={testid}
        value={q}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div
          data-testid={testid ? `${testid}-dropdown` : "employee-autocomplete-dropdown"}
          className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
        >
          {matches.map((e) => (
            <button
              key={e.id}
              type="button"
              data-testid={testid ? `${testid}-opt-${e.id}` : `employee-opt-${e.id}`}
              onClick={() => pick(e)}
              className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-sm border-b last:border-b-0 border-slate-100"
            >
              <span className="font-semibold text-slate-900">{e.name}</span>
              {e.position && <span className="text-slate-500"> · {e.position}</span>}
              {e.crew && e.crew !== "N/A" && <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-2">Cuadrilla {e.crew}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
